import { useState } from 'react';
import { Handshake, Loader2, Sparkles, ExternalLink, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * OneToOneBriefButton — bouton "🎯 Prépare mon 1-to-1"
 * -----------------------------------------------------
 * À placer sur la fiche/modale d'un apporteur. Uatu génère en 2-3 sec :
 *   - 3 signaux factuels à travailler
 *   - 3 questions constructives à poser
 *   - 1 point positif à célébrer
 *
 * Rendu inline sous le bouton (pas de nouvelle modale — on reste dans le
 * contexte de la fiche apporteur).
 */
export default function OneToOneBriefButton({ apporteurId, apporteurName }) {
    const [loading, setLoading] = useState(false);
    const [brief, setBrief] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const handleClick = async () => {
        if (brief) {
            setExpanded((v) => !v);
            return;
        }
        setLoading(true);
        try {
            const res = await api.get(`/ai/manager/one-to-one/${apporteurId}`);
            setBrief(res.data);
            setExpanded(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Impossible de préparer le brief');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenInChat = () => {
        try {
            window.dispatchEvent(
                new CustomEvent('uatu:open-with-message', {
                    detail: {
                        message: `Approfondis mon brief 1-to-1 avec ${apporteurName || 'cet apporteur'} — donne-moi 3 conseils supplémentaires pour rendre ce point efficace.`,
                    },
                })
            );
        } catch { /* noop */ }
    };

    return (
        <div className="mt-3" data-testid="one-to-one-brief-container">
            <button
                type="button"
                onClick={handleClick}
                disabled={loading}
                data-testid="prepare-one-to-one-btn"
                className="group inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold shadow-md hover:shadow-lg transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-wait"
                style={{
                    background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #A855F7 100%)',
                }}
            >
                {loading ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uatu prépare le brief…
                    </>
                ) : (
                    <>
                        <Handshake className="w-4 h-4 group-hover:rotate-6 transition-transform" />
                        {brief ? (expanded ? 'Masquer le brief' : 'Voir le brief 1-to-1') : 'Prépare mon 1-to-1'}
                    </>
                )}
            </button>

            {expanded && brief && (
                <div
                    className="mt-3 rounded-2xl border border-violet-200 overflow-hidden shadow-sm"
                    style={{
                        background: 'linear-gradient(135deg, rgba(76, 29, 149, 0.02) 0%, rgba(168, 85, 247, 0.05) 100%)',
                    }}
                    data-testid="one-to-one-brief-content"
                >
                    <div className="p-4 space-y-4">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-violet-600" />
                            <p className="text-xs font-bold text-violet-800 uppercase tracking-widest">
                                Uatu · Brief 1-to-1 avec {brief.apporteur_name}
                            </p>
                        </div>

                        {/* SIGNAUX */}
                        <div>
                            <p className="text-xs font-bold text-slate-700 mb-2">🎯 3 signaux à travailler</p>
                            {brief.signaux && brief.signaux.length > 0 ? (
                                <ul className="space-y-2">
                                    {brief.signaux.map((s, i) => (
                                        <li key={i} className="bg-white/80 border border-violet-100 rounded-lg p-3 text-sm">
                                            <p className="font-semibold text-slate-800">{s.titre}</p>
                                            <p className="text-xs text-slate-600 mt-0.5">{s.detail}</p>
                                            {s.deep_link && (
                                                <a
                                                    href={s.deep_link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-xs text-violet-700 hover:underline mt-1 inline-flex items-center gap-1"
                                                >
                                                    Voir <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-emerald-700 italic bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                    Aucun signal préoccupant — profite du point pour renforcer la relation.
                                </p>
                            )}
                        </div>

                        {/* QUESTIONS */}
                        <div>
                            <p className="text-xs font-bold text-slate-700 mb-2">❓ 3 questions à poser</p>
                            <ol className="space-y-2 list-decimal list-inside">
                                {(brief.questions || []).map((q, i) => (
                                    <li key={i} className="text-sm text-slate-800 bg-white/80 border border-violet-100 rounded-lg p-3">
                                        {q}
                                    </li>
                                ))}
                            </ol>
                        </div>

                        {/* POINT POSITIF */}
                        {brief.point_positif && (
                            <div>
                                <p className="text-xs font-bold text-slate-700 mb-2">✨ Point positif à célébrer</p>
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                    <p className="text-sm font-semibold text-emerald-900">{brief.point_positif.titre}</p>
                                    <p className="text-xs text-emerald-800 mt-1">{brief.point_positif.message}</p>
                                </div>
                            </div>
                        )}

                        {/* Bouton pour approfondir avec Uatu */}
                        <div className="flex items-center gap-2 pt-2 border-t border-violet-100">
                            <button
                                type="button"
                                onClick={handleOpenInChat}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-violet-800 bg-white hover:bg-violet-100 border border-violet-200 transition-colors"
                                data-testid="one-to-one-open-chat"
                            >
                                <MessageSquare className="w-3.5 h-3.5" />
                                Approfondis avec Uatu
                            </button>
                            <span className="text-[10px] text-slate-500 italic ml-auto">
                                Écoute d&apos;abord, agis ensuite · 15-20 min max
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
